import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useCrypto } from "./CryptoContext";
import { openConsultationPdf } from "./consultationPdf";

export interface ConsultationPdfButtonProps {
  consultationId: string;
  /** Whether the PDF has synced from the pharmacist's device yet. */
  hasPdf: boolean;
  /**
   * The treating pharmacist's PSI number. Recent PDFs use this as their
   * standard PDF open password; legacy PDFs without one remain unprotected.
   */
  pdfPassword?: string;
  label?: string;
  title?: string;
}

/**
 * "View PDF report" button + in-app viewer, shared by Chain Admin and
 * Pharmacy Admin consultation detail pages.
 *
 * Consultation PDFs are E2EE, so viewing one needs an async fetch + decrypt
 * (see ./consultationPdf.ts). We deliberately do *not* pre-open a tab for the
 * result: that showed the admin a blank tab for the whole decrypt window, and
 * a post-await `window.open` has lost user activation and gets popup-blocked.
 * Instead the button owns the loading state and the decrypted blob is rendered
 * in a dialog iframe, which gives the native browser PDF toolbar (zoom, print,
 * download) with no popups involved. "Open in new tab" inside the dialog is a
 * real user gesture, so it is allowed.
 */
export function ConsultationPdfButton({
  consultationId,
  hasPdf,
  pdfPassword,
  label = "View PDF report",
  title = "Consultation PDF report",
}: ConsultationPdfButtonProps) {
  const { isReady, decryptFileBytes } = useCrypto();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const mountedRef = useRef(true);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const handleClick = () => {
    // Already decrypted this consultation's report — reopen instantly rather
    // than re-fetching (which would also write a second audit entry).
    if (blobUrl) {
      setOpen(true);
      return;
    }
    setLoading(true);
    setError(null);
    openConsultationPdf(consultationId, decryptFileBytes)
      .then((url) => {
        if (!mountedRef.current) {
          URL.revokeObjectURL(url);
          return;
        }
        blobUrlRef.current = url;
        setBlobUrl(url);
        setOpen(true);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : "Could not open the PDF.");
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  };

  return (
    <>
      <Stack spacing={1}>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />
          }
          onClick={handleClick}
          disabled={loading || !hasPdf || !isReady}
          sx={{ alignSelf: "flex-start" }}
        >
          {loading ? "Decrypting…" : label}
        </Button>
        {!hasPdf ? (
          <Typography variant="caption" color="text.secondary">
            PDF has not synced from the pharmacist's device yet.
          </Typography>
        ) : null}
        {error ? (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        ) : null}
      </Stack>

      <Dialog
        open={open && !!blobUrl}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, minWidth: 0 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                if (blobUrl) window.open(blobUrl, "_blank");
              }}
            >
              Open in new tab
            </Button>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              aria-label="Close"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {pdfPassword?.trim() ? (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "info.50",
                borderBottom: 1,
                borderColor: "info.100",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                This PDF is password protected.
              </Typography>
              <Typography variant="body2">
                When prompted by the PDF viewer, enter the treating pharmacist’s PSI number:{" "}
                <strong>{pdfPassword.trim()}</strong>
              </Typography>
            </Box>
          ) : null}
          {blobUrl ? (
            <Box
              component="iframe"
              src={blobUrl}
              title={title}
              sx={{ display: "block", width: "100%", height: "80vh", border: 0 }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
